import React, { useState,useEffect } from 'react';
import { useDispatch,useSelector } from 'react-redux';
import {useParams} from 'react-router-dom';

import { useNotification } from 'config/Notification';

import { fetchReportsQuery as fetchReportsQueryService,fetchReportIds as fetchReportIdsService, uploadFiles as uploadFilesService } from 'services/modules/rhymscan/report';
import UploadIcon from '@mui/icons-material/Upload';
import { CloudUploadOutlined} from '@ant-design/icons';
import { Upload} from 'antd';

import { 
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select 
} from '@mui/material';

function UploadFiles() {

  // Redux dispatch
  const dispatch = useDispatch();

  // Dragger component
  const { Dragger } = Upload;

  // useParams hook
  const {reportId}=useParams();

  // Notification
  const {openNotification}=useNotification();

  // State variables
  const [fileList, setFileList] = useState([]);
  const [loading,setLoading]=useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); 
  const [progress,setProgress]=useState(0);
  const [reportIds, setReportIds] = useState([]);
  const [reportsCount,setReportsCount]=useState(0);
  const [totalReportsCount, setTotalReportsCount] = useState(0);
  const [requestReport,setRequestReport]=useState({});
  const [selectedReportId, setSelectedReportId] = useState(reportId);
  const [uploadedFiles,setUploadedFiles]=useState([]);
  const [uploads,setUploads]=useState(false);
 
  // Redux selectors
  const dimensionsContainer=useSelector((state)=>state.rsData.dimensions);

  // This method handles all the files before uploading
  const handleBeforeUpload = (file) => {
    return false
  };

  // This method clears the file from filelist when file is removed by user
  const handleRemove = (file) => {
      setFileList((prevFileList) => prevFileList.filter((f) => f.uid !== file.uid));
  };

  // This method appends all the uploaded files to filelist
  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // This method handles file upload 
  const handleFileUpload = async () => {
    if (fileList && fileList.length) {
      setLoading(true)
      try {
        const formData = new FormData();
        for (let index = 0; index < fileList.length; index++) {
          const file = fileList[index].originFileObj;
          const content = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsText(file);
          });
          const blob = new Blob([content], { type: "text/csv" });
          formData.append('data', blob, file.name);
        }
        const response = await uploadFilesService(requestReport.id, formData);
        if (response?.status === 200 || response?.status === 201) {
          openNotification('Files uploaded successfully', 'success');
        } else {
          openNotification(response || 'Error while uploading files', 'error');
        }
      } finally{
        setFileList([]);
        setLoading(false);
        setUploads(!uploads);
      }
    } else{
      openNotification("Please upload csv data files",'error')
    }
  };
      
  // This method fetches reports data based on report id
  const fetchReportsData = async () => {
    try {
      const response =await fetchReportsQueryService({ reportId });
      setRequestReport(response);
      setReportsCount(response?.progress?.length || 0);
      const reportIdsResponse = await dispatch(fetchReportIdsService());
      setReportIds(reportIdsResponse.payload?.results);
      if (!reportIds?.find((report) => report.reportId === reportId)) {
        setSelectedReportId(reportId);
      } else {
        setSelectedReportId(reportId); 
      }
    } catch (error) {
      openNotification('Server error', 'error');
    }
  };

  useEffect(() => {
    if (reportsCount > 0 && totalReportsCount > 0)
    setProgress( (reportsCount/totalReportsCount)*100);
  }, [reportsCount, totalReportsCount]);

  useEffect(() => {
    if (reportId !== null) 
      fetchReportsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, uploads]);

  // useEffect hook sets pending files and uploaded files
  useEffect(() => {
    if (dimensionsContainer && (dimensionsContainer.length >0) && requestReport && requestReport?.progress) {
      let activeDimensions = dimensionsContainer.filter(dim=>dim.status===true && dim.subDimensions);
      const filtereddimensions = activeDimensions.map(dim => dim.subDimensions).flat().filter(subDim => subDim.status === true);
      setTotalReportsCount(filtereddimensions.length);
      const pending=filtereddimensions.filter((dim) => !requestReport.progress.find((prog) => prog.dimension.id === dim.id))
      const uploaded =filtereddimensions.filter((dim) => requestReport.progress.find((prog) => prog.dimension.id === dim.id))
      setPendingFiles(pending);
      setUploadedFiles(uploaded);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestReport,uploads,dimensionsContainer]);
 
  return (
    <div className="main-cont">
      <Card className="card">
        <CardHeader
          title={
            <React.Fragment>
              <div className='card-head'>
                <div className="flex-head">
                  <label className='label'>
                    <UploadIcon className="upload-icon" /> 
                    Upload Files
                  </label>
                </div>
                <div>
                  <Button variant="contained" className="back font" onClick={() => window.history.back()}>
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <Divider className="divider" />
        <CardContent>
          <div className="flex">
            <LinearProgress variant="determinate" value={progress} className="linear-progress" color="secondary"/>
            <p className="reports-count">{reportsCount}/{totalReportsCount}</p>
          </div>
          <div className="row">
            <div className="col-md-6 col-lg-6 col-sm-12 col-xs-12">
              <FormControl className="form-control-dropdown">
                <InputLabel htmlFor="select-report-id">Select report id</InputLabel>
                <Select
                  value={selectedReportId}
                  onChange={(e) => setSelectedReportId(e.target.value)}
                  label="Select Report ID"
                  inputProps={{
                    name: 'reportId',
                    id: 'select-report-id',
                  }}
                >
                  <MenuItem value={reportId}> {reportId} </MenuItem>
                  {reportIds && reportIds.map((report) => (
                    <MenuItem key={report.id} value={report.reportId} disabled>
                      {report.reportId}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div className="upload-container">
                <Button 
                  variant="contained" 
                  className="upload-btn" 
                  onClick={handleFileUpload} 
                  diasbled={loading}
                  startIcon={loading && <CircularProgress size={20} sx={{ color: 'white' }} />} 
                  sx={{ color: loading && 'transparent' }} 
                >
                  {loading ? 'Uploading' : 'Upload Files'}
                </Button>
              </div>
              <div>
                <div className="mt-lg">
                  {pendingFiles.length ? (
                    <React.Fragment>
                      <p>Pending Files:</p>
                        <span className="file-list">
                          {pendingFiles.map((file) => (
                            <li key={file.slug}>{file.label}</li>
                          ))}
                        </span>
                    </React.Fragment>
                  ) : (
                    <p>No pending files</p>
                  )}
                </div>
              </div>     
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
              <div>
                <Dragger
                  className="upload-demo"
                  multiple
                  accept=".csv" 
                  autoUpload={false} 
                  beforeUpload={handleBeforeUpload}
                  onRemove={handleRemove}
                  onChange={handleUploadChange}
                  fileList={fileList}
                  showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                >
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined />
                  </p>
                  <p className="ant-upload-text">Drop file here or click to upload</p>
                </Dragger>
              </div>
              <p className="tip-message">upload csv files with a size less than 500kb</p>
              <div className="flex">
                {uploadedFiles.length ? (
                  <React.Fragment>
                    <div className="col-lg-11">
                      <div className="file-list">
                        {uploadedFiles.map((file) => (
                          <li key={file.slug}>{file.slug}</li>
                        ))}
                      </div>
                    </div>
                    <div className="col-lg-1">
                      {uploadedFiles.map((file) => (
                        <div key={file.slug} className="success">
                          &#10004;
                        </div>
                      ))}
                    </div>
                  </React.Fragment>
                ) : (
                  <p></p>
                )}
              </div>
            </div>      
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default UploadFiles;